<template>
  <v-app>
    <AppHeaderComponent></AppHeaderComponent>
    <AppSidebarComponent :is-open="isSidebarOpen" @compose="showComposeDialog = true"></AppSidebarComponent>
    
    <!-- Alert Component -->
    <AlertComponent />
    
    <v-main>
      <v-container fluid>
        <router-view @openComposeReply="handleComposeReply" @openComposeForward="handleComposeForward"></router-view>
      </v-container>
    </v-main>
    
    <!-- Compose Dialog -->
    <v-dialog v-model="showComposeDialog" max-width="800">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <span>{{ getDialogTitle() }}</span>
            <v-chip 
              v-if="isReplyMode" 
              class="ml-3" 
              color="success" 
              variant="outlined" 
              size="small"
            >
              <v-icon start size="small">mdi-reply</v-icon>
              رد
            </v-chip>
            <v-chip 
              v-if="isForwardMode" 
              class="ml-3" 
              color="info" 
              variant="outlined" 
              size="small"
            >
              <v-icon start size="small">mdi-forward</v-icon>
              إعادة توجيه
            </v-chip>
            <v-chip 
              v-if="isPermissionFiltered && !isReplyMode && !isForwardMode" 
              class="ml-3" 
              color="info" 
              variant="outlined" 
              size="small"
            >
              <v-icon start size="small">mdi-shield-check</v-icon>
              مُقيد بالصلاحيات
            </v-chip>
          </div>
          <v-btn icon variant="text" @click="closeComposeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <!-- Send permissions info -->
        <v-alert
          v-if="isPermissionFiltered"
          type="info"
          variant="tonal"
          density="compact"
          class="ma-4 mb-2"
        >
          <template v-slot:prepend>
            <v-icon>mdi-information</v-icon>
          </template>
          <div class="text-body-2">
            <strong>صلاحيات الإرسال نشطة:</strong>
            يمكنك إرسال رسائل إلى {{ availableDepartments.length }} أقسام فقط حسب صلاحيات قسمك الحالي
          </div>
        </v-alert>
        
        <v-card-text class="pt-4">
          <v-form ref="composeForm" @submit.prevent="sendMessage">
            <v-autocomplete
              v-model="newMessage.to_departments"
              :items="(isReplyMode || isForwardMode) ? availableDepartments : availableDepartments"
              label="إلى الأقسام"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              multiple
              chips
              closable-chips
              :rules="[v => (v && v.length > 0) || 'يجب تحديد قسم واحد على الأقل']"
              :hint="getToFieldHint()"
              persistent-hint
              :disabled="isReplyMode || availableDepartments.length === 0"
              :readonly="isReplyMode"
            >
              <template v-slot:chip="{ props, item }">
                <v-chip
                  v-bind="props"
                  :text="item.raw.name"
                  class="ma-1"
                  color="primary"
                  variant="outlined"
                ></v-chip>
              </template>
            </v-autocomplete>
            
            <v-autocomplete
              v-model="newMessage.cc_departments"
              :items="(isReplyMode || isForwardMode) ? availableDepartments : availableDepartments"
              label="نسخة إلى الأقسام (CC)"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              multiple
              chips
              closable-chips
              :hint="getCcFieldHint()"
              persistent-hint
              :disabled="isReplyMode || availableDepartments.length === 0"
              :readonly="isReplyMode"
            >
              <template v-slot:chip="{ props, item }">
                <v-chip
                  v-bind="props"
                  :text="item.raw.name"
                  class="ma-1"
                  color="secondary"
                  variant="outlined"
                ></v-chip>
              </template>
            </v-autocomplete>
            
            <v-text-field
              v-model="newMessage.subject"
              label="الموضوع"
              variant="outlined"
              class="mb-4"
              required
              :readonly="isReplyMode || isForwardMode"
              :rules="[v => !!v || 'الموضوع مطلوب']"
            ></v-text-field>
            
            <!-- Mail Priority -->
            <v-select
              v-model="newMessage.mail_priority_id"
              :items="mailPriorities"
              label="أولوية المراسلة"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              :rules="[v => !!v || 'أولوية المراسلة مطلوبة']"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon :color="item.raw.color">mdi-flag</v-icon>
                  </template>
                  <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.raw.desc }}</v-list-item-subtitle>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip :color="item.raw.color" variant="outlined" size="small">
                  <v-icon start>mdi-flag</v-icon>
                  {{ item.raw.name }}
                </v-chip>
              </template>
            </v-select>
            
            <!-- Mail Type -->
            <v-select
              v-model="newMessage.mail_type_id"
              :items="mailTypes"
              label="نوع المراسلة"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              :rules="mailTypes.length > 0 ? [v => !!v || 'نوع المراسلة مطلوب'] : []"
              :disabled="mailTypes.length === 0"
              :hint="mailTypes.length === 0 ? 'لا توجد أنواع مراسلات متاحة' : `${mailTypes.length} نوع متاح`"
              persistent-hint
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-email-variant</v-icon>
                  </template>
                  <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.raw.desc">{{ item.raw.desc }}</v-list-item-subtitle>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip color="primary" variant="outlined" size="small">
                  <v-icon start>mdi-email-variant</v-icon>
                  {{ item.raw.name }}
                </v-chip>
              </template>
            </v-select>
       
            
            <!-- Action Buttons -->
            <div class="d-flex align-center mb-4 gap-2">
              <v-btn
                prepend-icon="mdi-file-document-multiple"
                variant="outlined"
                color="primary"
                @click="showTemplateDialog = true"
              >
                استخدام قالب
              </v-btn>
              
              <v-btn
                v-if="hasPermission('admin.mails.store-draft')"
                prepend-icon="mdi-content-save-outline"
                variant="outlined"
                color="secondary"
                @click="saveDraft"
                :loading="savingDraft"
              >
                حفظ كمسودة
              </v-btn>
              
              <span v-if="selectedTemplate" class="text-caption text-grey ms-2">
                القالب المحدد: {{ selectedTemplate.name }}
              </span>
              
              <span v-if="draftSaved" class="text-caption text-success ms-2">
                <v-icon size="small" color="success">mdi-check</v-icon>
                تم حفظ المسودة
              </span>
            </div>
            
            <!-- Enhanced Formatting Toolbar -->
            <v-card flat class="mb-4 formatting-toolbar">
              <v-toolbar density="compact" color="grey-lighten-4">
                <!-- Text Formatting -->
                <v-btn-group density="comfortable">
                  <v-btn
                    icon
                    variant="text"
                    v-for="(action, index) in textFormatActions"
                    :key="index"
                    :title="action.title"
                    @click="formatText(action.command)"
                  >
                    <v-icon>{{ action.icon }}</v-icon>
                  </v-btn>
                </v-btn-group>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <!-- Font Size -->
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                      title="حجم الخط"
                    >
                      <v-icon>mdi-format-size</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item
                      v-for="size in fontSizes"
                      :key="size.value"
                      @click="setFontSize(size.value)"
                    >
                      <v-list-item-title :style="{ fontSize: size.preview }">
                        {{ size.label }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                
                <!-- Text Color -->
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                      title="لون النص"
                    >
                      <v-icon>mdi-format-color-text</v-icon>
                    </v-btn>
                  </template>
                  <v-card min-width="200">
                    <v-card-text class="pa-2">
                      <div class="d-flex flex-wrap">
                        <div
                          v-for="color in textColors"
                          :key="color.value"
                          class="color-picker-item ma-1"
                          :style="{ backgroundColor: color.value }"
                          @click="setTextColor(color.value)"
                          :title="color.name"
                        ></div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <!-- Alignment -->
                <v-btn-group density="comfortable">
                  <v-btn
                    icon
                    variant="text"
                    title="محاذاة لليمين"
                    @click="setAlignment('justifyRight')"
                  >
                    <v-icon>mdi-format-align-right</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    title="محاذاة للوسط"
                    @click="setAlignment('justifyCenter')"
                  >
                    <v-icon>mdi-format-align-center</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    title="محاذاة لليسار"
                    @click="setAlignment('justifyLeft')"
                  >
                    <v-icon>mdi-format-align-left</v-icon>
                  </v-btn>
                </v-btn-group>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <!-- Lists -->
                <v-btn-group density="comfortable">
                  <v-btn
                    icon
                    variant="text"
                    title="قائمة مرقمة"
                    @click="insertList('ordered')"
                  >
                    <v-icon>mdi-format-list-numbered</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    title="قائمة نقطية"
                    @click="insertList('unordered')"
                  >
                    <v-icon>mdi-format-list-bulleted</v-icon>
                  </v-btn>
                </v-btn-group>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <!-- Insert Options -->
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                      title="إدراج"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="insertTable">
                      <template v-slot:prepend>
                        <v-icon>mdi-table</v-icon>
                      </template>
                      <v-list-item-title>جدول</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="insertLink">
                      <template v-slot:prepend>
                        <v-icon>mdi-link</v-icon>
                      </template>
                      <v-list-item-title>رابط</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="insertImage">
                      <template v-slot:prepend>
                        <v-icon>mdi-image</v-icon>
                      </template>
                      <v-list-item-title>صورة</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="insertChart">
                      <template v-slot:prepend>
                        <v-icon>mdi-chart-line</v-icon>
                      </template>
                      <v-list-item-title>رسم بياني</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="insertSignature">
                      <template v-slot:prepend>
                        <v-icon>mdi-signature</v-icon>
                      </template>
                      <v-list-item-title>توقيع</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-toolbar>
            </v-card>
            
            <!-- Body Content Editor -->
            <div class="mb-4">
              <label class="text-subtitle-2 text-grey-darken-2 mb-2 d-block">محتوى المراسلة</label>
              <div
                ref="editor"
                class="editor"
                contenteditable="true"
               
                @focusout="updateContent"
                v-html="newMessage.body"
                placeholder="اكتب محتوى المراسلة هنا..."
                :style="{ minHeight: '200px', direction: 'rtl' }"
              ></div>
            </div>
            
            <!-- Attachments -->
            <div class="mb-4">
              <label class="text-subtitle-2 text-grey-darken-2 mb-2 d-block">المرفقات</label>
              <div class="d-flex align-center flex-wrap gap-2">
                <v-btn
                  prepend-icon="mdi-paperclip"
                  variant="outlined"
                  color="primary"
                  @click="$refs.fileInput.click()"
                >
                  إضافة مرفقات
                </v-btn>
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  class="d-none"
                  @change="handleFileUpload"
                 
                >
                
                <!-- Attachment chips -->
                <template v-if="newMessage.attachments.length">
                  <v-chip
                    v-for="(file, i) in newMessage.attachments"
                    :key="i"
                    closable
                    color="info"
                    variant="outlined"
                    class="ma-1"
                    @click:close="removeAttachment(i)"
                  >
                    <v-icon start>{{ getFileIcon(file.name) }}</v-icon>
                    {{ file.name }}
                    <span class="text-caption ml-1">({{ formatFileSize(file.size) }})</span>
                  </v-chip>
                </template>
              </div>
              
              <!-- Attachment limit info -->
              <div class="text-caption text-grey mt-2">
                الحد الأقصى لحجم الملف: 10 ميجابايت. الأنواع المدعومة: PDF, Word, Excel, PowerPoint, الصور, النصوص
              </div>
            </div>
            
            <!-- Original Mail Card (Reply/Forward Mode) -->
            <div v-if="(isReplyMode && replyData && replyData.originalMail) || (isForwardMode && forwardData && forwardData.originalMail)" class="mt-4">
              <v-card variant="outlined" class="mb-4">
                <v-card-title class="text-subtitle-1 d-flex align-center">
                  <v-icon class="me-2" color="info">mdi-email</v-icon>
                  الرسالة الأصلية
                  <v-spacer></v-spacer>
                  <v-chip size="small" color="info" variant="outlined">
                    {{ isReplyMode ? 'رد على' : 'إعادة توجيه' }}
                  </v-chip>
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="success"
                    @click="printOriginalMessage"
                    :title="'طباعة الرسالة الأصلية'"
                  >
                    <v-icon size="small">mdi-printer</v-icon>
                  </v-btn>
                </v-card-title>
                
                <v-card-text class="pt-2">
                  <div class="d-flex flex-column ga-2">
                    <!-- Original mail details -->
                    <div class="d-flex align-center">
                      <v-icon class="me-2" size="small">mdi-account</v-icon>
                      <span class="text-body-2"><strong>من:</strong> {{ getOriginalMail().sender?.name || 'غير محدد' }}</span>
                      <v-spacer></v-spacer>
                      <span class="text-caption text-grey">{{ formatDate(getOriginalMail().sent_at || getOriginalMail().created_at) }}</span>
                    </div>
                    
                    <div class="d-flex align-center">
                      <v-icon class="me-2" size="small">mdi-tag</v-icon>
                      <span class="text-body-2"><strong>الموضوع:</strong> {{ getOriginalMail().subject }}</span>
                    </div>
                    
                    <!-- Original content preview -->
                    <div class="mt-2">
                      <v-divider class="mb-2"></v-divider>
                      <div class="text-body-2 text-grey-darken-1" style="max-height: 100px; overflow-y: auto;">
                        {{ getContentPreview(getOriginalMail().body) }}
                      </div>
                    </div>
                    
                    <!-- Original attachments if any -->
                    <div v-if="getOriginalMail().attachments && getOriginalMail().attachments.length > 0" class="mt-2">
                      <div class="text-caption text-grey mb-1">مرفقات الرسالة الأصلية:</div>
                      <div class="d-flex flex-wrap ga-1">
                        <v-chip
                          v-for="attachment in getOriginalMail().attachments"
                          :key="attachment.id"
                          size="x-small"
                          color="grey"
                          variant="outlined"
                        >
                          <v-icon start size="x-small">mdi-paperclip</v-icon>
                          {{ attachment.file_name || 'ملف' }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-form>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showComposeDialog = false"
          >
            إلغاء
          </v-btn>
          <v-btn
             v-if="hasPermission('admin.mails.store')"
            color="primary"
            :loading="sending"
            @click="sendMessage"
          >
            إرسال
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Template Selection Dialog -->
    <v-dialog v-model="showTemplateDialog" max-width="800px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>اختيار قالب</span>
          <v-btn icon variant="text" @click="showTemplateDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <v-text-field
            v-model="templateSearch"
            prepend-inner-icon="mdi-magnify"
            label="بحث في القوالب"
            variant="outlined"
            density="compact"
            class="mb-4"
          ></v-text-field>
          
          <v-row>
            <v-col
              v-for="template in filteredTemplates"
              :key="template.id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                class="template-card"
                :class="{ 'template-card--selected': selectedTemplate?.id === template.id }"
                @click="selectTemplate(template)"
              >
                <v-card-text class="text-center pa-4">
                  <v-icon size="32" color="primary" class="mb-2">mdi-file-document</v-icon>
                  <div class="text-h6 mb-2">{{ template.name }}</div>
                  <div class="text-body-2 text-grey mb-3">{{ template.desc || template.description || 'لا يوجد وصف' }}</div>
                  <div class="text-caption text-grey">{{ template.category?.name || 'عام' }}</div>
                </v-card-text>
                
                <v-card-actions class="justify-center pa-3">
                  <v-btn
                    size="small"
                    color="primary"
                    variant="outlined"
                    @click.stop="previewTemplate(template)"
                  >
                    معاينة
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
          
          <div v-if="filteredTemplates.length === 0" class="text-center pa-8">
            <v-icon size="48" color="grey-lighten-1">mdi-file-document-outline</v-icon>
            <div class="text-h6 mt-4 text-grey">لا توجد قوالب</div>
            <div class="text-body-2 text-grey">قم بإنشاء قوالب من صفحة إدارة القوالب</div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showTemplateDialog = false"
          >
            إلغاء
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedTemplate"
            @click="applyTemplate"
          >
            تطبيق القالب
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Template Preview Dialog -->
    <v-dialog v-model="templatePreviewDialog" max-width="600px">
      <v-card v-if="previewTemplateData">
        <v-card-title>معاينة القالب: {{ previewTemplateData.name }}</v-card-title>
        <v-card-text>
          <div class="mb-4">
            <strong>الموضوع:</strong> {{ previewTemplateData.subject }}
          </div>
          <div class="mb-4">
            <strong>المحتوى:</strong>
            <div class="mt-2 p-3 border rounded" v-html="previewTemplateData.body || previewTemplateData.content"></div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="templatePreviewDialog = false"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Table Insert Dialog -->
    <v-dialog v-model="tableDialog" max-width="400px">
      <v-card>
        <v-card-title>إدراج جدول</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="tableConfig.rows"
                label="عدد الصفوف"
                type="number"
                min="1"
                max="10"
                variant="outlined"
                density="compact"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="tableConfig.columns"
                label="عدد الأعمدة"
                type="number"
                min="1"
                max="10"
                variant="outlined"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="tableDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="insertTableConfirm">إدراج</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Link Insert Dialog -->
    <v-dialog v-model="linkDialog" max-width="400px">
      <v-card>
        <v-card-title>إدراج رابط</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="linkConfig.url"
            label="الرابط"
            variant="outlined"
            density="compact"
            placeholder="https://example.com"
          ></v-text-field>
          <v-text-field
            v-model="linkConfig.text"
            label="نص الرابط"
            variant="outlined"
            density="compact"
            placeholder="النص المعروض"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="linkDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="insertLinkConfirm">إدراج</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import AppHeaderComponent from '@/components/AppHeaderComponent.vue'
import AppSidebarComponent from '@/components/AppSidebarComponent.vue'
import AlertComponent from '@/components/AlertComponent.vue'
import mailPriorityService from '@/services/mailPriorityService'
import mailTemplateService from '@/services/mailTemplateService'
import departmentService from '@/services/departmentService'
import mailTypeService from '@/services/mailTypeService'
import { showSuccessAlert, showErrorAlert, showWarningAlert } from '@/utils/alertUtils'
import {  hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'DashboardLayout',
  components: {
    AppHeaderComponent,
    AppSidebarComponent,
    AlertComponent
  },
  data() {
    return {
      showComposeDialog: false,
      sending: false,
      savingDraft: false,
      draftSaved: false,
      newMessage: {
        to_departments: [],
        cc_departments: [],
        subject: '',
        body: '',
        mail_priority_id: null,
        mail_type_id: null,
        attachments: []
      },
      // Reply data
      replyData: null,
      isReplyMode: false,
      // Forward data
      forwardData: null,
      isForwardMode: false,
      allDepartments: [],
      mailPriorities: [],
      mailTypes: [],
      textFormatActions: [
        { icon: 'mdi-format-bold', command: 'bold', title: 'غامق' },
        { icon: 'mdi-format-italic', command: 'italic', title: 'مائل' },
        { icon: 'mdi-format-underline', command: 'underline', title: 'تحته خط' },
        { icon: 'mdi-format-strikethrough', command: 'strikeThrough', title: 'يتوسطه خط' }
      ],
      fontSizes: [
        { label: 'صغير جداً', value: '1', preview: '10px' },
        { label: 'صغير', value: '2', preview: '12px' },
        { label: 'عادي', value: '3', preview: '14px' },
        { label: 'كبير', value: '4', preview: '16px' },
        { label: 'كبير جداً', value: '5', preview: '18px' }
      ],
      textColors: [
        { name: 'أسود', value: '#000000' },
        { name: 'أحمر', value: '#FF0000' },
        { name: 'أخضر', value: '#008000' },
        { name: 'أزرق', value: '#0000FF' },
        { name: 'أصفر', value: '#FFFF00' },
        { name: 'برتقالي', value: '#FFA500' },
        { name: 'بنفسجي', value: '#800080' },
        { name: 'رمادي', value: '#808080' },
        { name: 'بني', value: '#A52A2A' },
        { name: 'وردي', value: '#FFC0CB' }
      ],
      templates: [],
      showTemplateDialog: false,
      templateSearch: '',
      selectedTemplate: null,
      templatePreviewDialog: false,
      previewTemplateData: null,
      tableDialog: false,
      tableConfig: {
        rows: 3,
        columns: 3
      },
      linkDialog: false,
      linkConfig: {
        url: '',
        text: ''
      }
    }
  },
  computed: {
    ...mapState(['isSidebarOpen']),
    ...mapGetters(['selectedDepartment', 'sendPermissions']),
    
    // Filter departments based on current department's send permissions
    availableDepartments() {
      if (!this.allDepartments || this.allDepartments.length === 0) {
        return [];
      }
      
      // Debug logging in development mode only
      if (process.env.NODE_ENV === 'development') {
        //console.log('🏢 All departments:', this.allDepartments.length);
        //console.log('📤 Send permissions from store:', this.sendPermissions);
        //console.log('🎯 Selected department:', this.selectedDepartment?.department?.name);
      }
      
      // If no send permissions defined, allow sending to all active departments
      if (!this.sendPermissions || this.sendPermissions.length === 0) {
        const activeDepts = this.allDepartments.filter(dept => dept.active !== false);
        if (process.env.NODE_ENV === 'development') {
          //console.log('✅ No send restrictions - showing all active departments:', activeDepts.length);
        }
        return activeDepts;
      }
      
      // Filter departments based on send permissions
      const filtered = this.allDepartments.filter(dept => {
        // Only include departments that are in the send permissions and are active
        const hasPermission = this.sendPermissions.includes(dept.id);
        const isActive = dept.active !== false;
        const allowed = hasPermission && isActive;
        
        if (process.env.NODE_ENV === 'development' && hasPermission && !isActive) {
          //console.log(`⚠️ Department ${dept.name} (${dept.id}) has permission but is inactive`);
        }
        
        return allowed;
      });
      
      if (process.env.NODE_ENV === 'development') {
        //console.log('🎯 Filtered departments based on send permissions:', filtered.length);
        //console.log('📋 Available departments:', filtered.map(d => `${d.name} (${d.id})`));
        //console.log('🔍 Send permissions filtering: Only showing departments with IDs:', this.sendPermissions);
      }
      
      return filtered;
    },
    
    // Check if send permissions are being applied
    isPermissionFiltered() {
      return this.sendPermissions && this.sendPermissions.length > 0;
    },
    
    filteredTemplates() {
      if (!this.templateSearch) {
        return this.templates;
      }
      const search = this.templateSearch.toLowerCase();
      return this.templates.filter(template => 
        template.name.toLowerCase().includes(search) ||
        (template.desc && template.desc.toLowerCase().includes(search)) ||
        (template.description && template.description.toLowerCase().includes(search)) ||
        template.category?.name.toLowerCase().includes(search)
      );
    },
    
    // Debug computed property for mail types
    mailTypesDebugInfo() {
      return {
        total: this.mailTypes.length,
        items: this.mailTypes,
        hasSelection: !!this.newMessage.mail_type_id,
        selectedId: this.newMessage.mail_type_id
      };
    }
  },
  
  async mounted() {
    //console.log('DashboardLayout mounted - starting data loading...');
    await this.loadDepartments();
    //console.log('Departments loaded, count:', this.allDepartments.length);
    await this.loadMailPriorities();
    //console.log('Mail priorities loaded, count:', this.mailPriorities.length);
    await this.loadMailTypes();
    //console.log('Mail types loaded, count:', this.mailTypes.length);
    await this.loadTemplates();
    //console.log('Templates loaded, count:', this.templates.length);
    //console.log('All data loading completed');
  },
  
  watch: {
    sendPermissions: {
      handler() {
        this.validateSelectedDepartments();
      },
      immediate: true
    },
    
    mailTypes: {
      handler() {
        //console.log('Mail types changed:', {
        //  old: oldVal?.length || 0,
        //  new: newVal?.length || 0,
        //  items: newVal
        //});
      },
      immediate: true
    },
    
    'newMessage.mail_type_id': {
      handler() {
        //console.log('Selected mail type changed:', {
        //  old: oldVal,
        //  new: newVal,
        //  selectedType: this.mailTypes.find(t => t.id === newVal)
        //});
      }
    }
  },
  methods: {
    ...mapMutations(['toggleSidebar']),
    
    async loadDepartments() {
      try {
        const response = await departmentService.getDepartments();
        this.allDepartments = response.data?.data || response.data || [];
      } catch (error) {
        console.error('Error loading departments:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل الأقسام');
      }
    },

    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    
    async loadMailPriorities() {
      try {
        const response = await mailPriorityService.getMailPriorities();
        this.mailPriorities = response.data || [];
      } catch (error) {
        console.error('Error loading mail priorities:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل أولويات البريد');
      }
    },

    async loadMailTypes() {
      try {
        //console.log('Loading mail types...');
        const response = await mailTypeService.getMailTypes();
        //console.log('Mail types API response:', response);
        
        // Handle different response structures
        let allMailTypes = [];
        if (response.data && Array.isArray(response.data)) {
          allMailTypes = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          allMailTypes = response.data.data;
        } else if (Array.isArray(response)) {
          allMailTypes = response;
        } else {
          console.warn('Unexpected mail types response structure:', response);
          allMailTypes = [];
        }
        
        //console.log('All mail types before filtering:', allMailTypes);
        
        // Filter only active mail types
        this.mailTypes = allMailTypes.filter(type => {
          const isActive = type.active === 1 || type.active === true || type.active === '1';
          //console.log(`Mail type ${type.name}: active=${type.active}, isActive=${isActive}`);
          return isActive;
        });
        
        //console.log('Filtered active mail types:', this.mailTypes);
        
        if (this.mailTypes.length === 0) {
          console.warn('No active mail types found');
        }
        
      } catch (error) {
        console.error('Error loading mail types:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل أنواع البريد');
        // Set empty array as fallback
        this.mailTypes = [];
      }
    },

    async loadTemplates() {
      try {
        const response = await mailTemplateService.getMailTemplates();
        //console.log('Templates response:', response);
        
        // Handle different response structures
        this.templates = response.data?.data || response.data || [];
        
        //console.log('Loaded templates:', this.templates);
      } catch (error) {
        console.error('Error loading templates:', error);
        showErrorAlert(this.$store, error, 'فشل في تحميل القوالب');
      }
    },
    
    validateSelectedDepartments() {
      // Remove any selected departments that are no longer available
      if (this.newMessage.to_departments && this.newMessage.to_departments.length > 0) {
        const availableIds = this.availableDepartments.map(dept => dept.id);
        this.newMessage.to_departments = this.newMessage.to_departments.filter(id => availableIds.includes(id));
      }
      
      if (this.newMessage.cc_departments && this.newMessage.cc_departments.length > 0) {
        const availableIds = this.availableDepartments.map(dept => dept.id);
        this.newMessage.cc_departments = this.newMessage.cc_departments.filter(id => availableIds.includes(id));
      }
    },
    
    async sendMessage() {
      // Validate form
      if (!this.$refs.composeForm || !this.$refs.composeForm.validate()) {
        return;
      }
      
      // Validate that at least one recipient is selected
      if (!this.newMessage.to_departments || this.newMessage.to_departments.length === 0) {
        showWarningAlert(this.$store, 'يرجى تحديد قسم واحد على الأقل');
        return;
      }
      
      this.sending = true;
      try {
        // Prepare form data for API call
        const formData = new FormData();
        this.newMessage.body = this.$refs.editor.innerHTML;
        // Add basic fields
        formData.append('subject', this.newMessage.subject);
        formData.append('body', this.newMessage.body);
        formData.append('mail_priority_id', this.newMessage.mail_priority_id);
        if (this.newMessage.mail_type_id) {
          formData.append('mail_type_id', this.newMessage.mail_type_id);
        }
        
        // Add to_departments array
        this.newMessage.to_departments.forEach((deptId, index) => {
          formData.append(`to_departments[${index}]`, deptId);
        });
        
        // Add cc_departments array if exists
        if (this.newMessage.cc_departments && this.newMessage.cc_departments.length > 0) {
          this.newMessage.cc_departments.forEach((deptId, index) => {
            formData.append(`cc_departments[${index}]`, deptId);
          });
        }
        
        // Add parent_id for replies
        if (this.isReplyMode && this.replyData && this.replyData.parent_id) {
          formData.append('parent_id', this.replyData.parent_id);
        }
        
        // Add forward_parent_id for forwards
        if (this.isForwardMode && this.forwardData && this.forwardData.forward_parent_id) {
          formData.append('forward_parent_id', this.forwardData.forward_parent_id);
        }
        
        // Add attachments if exists
        if (this.newMessage.attachments && this.newMessage.attachments.length > 0) {
          this.newMessage.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }
        
        // Import api client dynamically to avoid circular imports
        const { default: apiClient } = await import('@/services/api');
        
        // Make API call to send mail
        const response = await apiClient.post('/admin/mails', formData, {
         
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        //console.log(response);
        
        // Use backend message if available, otherwise create detailed success message
        const backendMessage = response.data?.message;
        let successMessage;
        
        if (backendMessage) {
          successMessage = backendMessage;
        } else {
          // Create custom success message with recipient info as fallback
          const toNames = this.newMessage.to_departments.map(id => 
            this.allDepartments.find(dept => dept.id === id)?.name
          ).filter(Boolean);
          
          const ccNames = this.newMessage.cc_departments.map(id => 
            this.allDepartments.find(dept => dept.id === id)?.name
          ).filter(Boolean);
          
          const priorityName = this.mailPriorities.find(p => p.id === this.newMessage.mail_priority_id)?.name;
          
          successMessage = `تم إرسال المراسلة بنجاح\nإلى: ${toNames.join(', ')}`;
          if (ccNames.length > 0) {
            successMessage += `\nنسخة إلى: ${ccNames.join(', ')}`;
          }
          if (priorityName) {
            successMessage += `\nالأولوية: ${priorityName}`;
          }
          if (this.newMessage.attachments && this.newMessage.attachments.length > 0) {
            successMessage += `\nالمرفقات: ${this.newMessage.attachments.length} ملف`;
          }
        }
        
        showSuccessAlert(this.$store, response, successMessage, { timeout: 5000 });
        
        this.showComposeDialog = false;
        this.resetForm();
      } catch (error) {
        console.error('Error sending mail:', error);
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء إرسال المراسلة', { timeout: 5000 });
      } finally {
        this.sending = false;
      }
    },
    
    removeAttachment(index) {
      this.newMessage.attachments.splice(index, 1);
      
      this.$store.dispatch('alerts/showAlert', {
        id: Date.now(),
        type: 'info',
        color: 'info',
        icon: 'mdi-paperclip-off',
        timeout: 2000,
        closable: true,
        message: 'تم حذف المرفق'
      });
    },
    
    formatText(command) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        // Try using execCommand first (for basic formatting)
        if (document.queryCommandSupported && document.queryCommandSupported(command)) {
          document.execCommand(command, false, null);
        } else {
          // Fallback for unsupported commands
          console.warn(`Command ${command} not supported, using alternative method`);
          
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            
            if (selectedText) {
              let wrapper;
              switch (command) {
                case 'bold':
                  wrapper = document.createElement('strong');
                  break;
                case 'italic':
                  wrapper = document.createElement('em');
                  break;
                case 'underline':
                  wrapper = document.createElement('u');
                  break;
                case 'strikeThrough':
                  wrapper = document.createElement('strike');
                  break;
                default:
                  return;
              }
              
              wrapper.textContent = selectedText;
              range.deleteContents();
              range.insertNode(wrapper);
            }
          }
        }
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error formatting text:', error);
      }
    },
    
    setFontSize(size) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        if (document.queryCommandSupported && document.queryCommandSupported('fontSize')) {
          document.execCommand('fontSize', false, size);
        } else {
          // Fallback method
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            
            if (selectedText) {
              const span = document.createElement('span');
              span.style.fontSize = `${parseInt(size) * 2 + 8}px`;
              span.textContent = selectedText;
              range.deleteContents();
              range.insertNode(span);
            }
          }
        }
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error setting font size:', error);
      }
    },
    
    setTextColor(color) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        if (document.queryCommandSupported && document.queryCommandSupported('foreColor')) {
          document.execCommand('foreColor', false, color);
        } else {
          // Fallback method
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            
            if (selectedText) {
              const span = document.createElement('span');
              span.style.color = color;
              span.textContent = selectedText;
              range.deleteContents();
              range.insertNode(span);
            }
          }
        }
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error setting text color:', error);
      }
    },
    
    setAlignment(command) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        if (document.queryCommandSupported && document.queryCommandSupported(command)) {
          document.execCommand(command, false, null);
        } else {
          // Fallback method
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const container = range.commonAncestorContainer;
            
            // Find the closest block element
            let blockElement = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
            while (blockElement && !['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(blockElement.tagName)) {
              blockElement = blockElement.parentElement;
            }
            
            if (blockElement) {
              switch (command) {
                case 'justifyRight':
                  blockElement.style.textAlign = 'right';
                  break;
                case 'justifyCenter':
                  blockElement.style.textAlign = 'center';
                  break;
                case 'justifyLeft':
                  blockElement.style.textAlign = 'left';
                  break;
              }
            }
          }
        }
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error setting alignment:', error);
      }
    },
    
    async saveDraft() {
      this.savingDraft = true;
      this.draftSaved = false;
      
      try {
        // Validate that required fields are present
        if (!this.newMessage.subject && !this.newMessage.body) {
          showWarningAlert(this.$store, 'لا يمكن حفظ مسودة فارغة');
          return;
        }
        
        // Prepare form data for API call
        const formData = new FormData();
        
        // Add basic fields
        formData.append('subject', this.newMessage.subject || '');
        formData.append('body', this.newMessage.body || '');
        formData.append('draft', '1'); // Mark as draft
        
        if (this.newMessage.mail_priority_id) {
          formData.append('mail_priority_id', this.newMessage.mail_priority_id);
        }
        
        if (this.newMessage.mail_type_id) {
          formData.append('mail_type_id', this.newMessage.mail_type_id);
        }
        
        // Add to_departments array
        if (this.newMessage.to_departments && this.newMessage.to_departments.length > 0) {
          this.newMessage.to_departments.forEach((deptId, index) => {
            formData.append(`to_departments[${index}]`, deptId);
          });
        }
        
        // Add cc_departments array if exists
        if (this.newMessage.cc_departments && this.newMessage.cc_departments.length > 0) {
          this.newMessage.cc_departments.forEach((deptId, index) => {
            formData.append(`cc_departments[${index}]`, deptId);
          });
        }
        
        // Add attachments if exists
        if (this.newMessage.attachments && this.newMessage.attachments.length > 0) {
          this.newMessage.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }
        
        // Import api client dynamically to avoid circular imports
        const { default: apiClient } = await import('@/services/api');
        
        // Make API call to save draft
        const response = await apiClient.post('/admin/mails', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.draftSaved = true;
        
        showSuccessAlert(this.$store, response, 'تم حفظ المسودة بنجاح', { icon: 'mdi-content-save' });
        
        // Hide the success indicator after 3 seconds
        setTimeout(() => {
          this.draftSaved = false;
        }, 3000);
        
      } catch (error) {
        console.error('Error saving draft:', error);
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء حفظ المسودة');
      } finally {
        this.savingDraft = false;
      }
    },

    updateContent(event) {
      this.newMessage.body = event.target.innerHTML;
    },
    
    resetForm() {
      this.newMessage = {
        to_departments: [],
        cc_departments: [],
        subject: '',
        body: '',
        mail_priority_id: null,
        mail_type_id: null,
        attachments: []
      };
      this.selectedTemplate = null;
      this.draftSaved = false;
      // Clear reply data
      this.replyData = null;
      this.isReplyMode = false;
      // Clear forward data
      this.forwardData = null;
      this.isForwardMode = false;
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = '';
      }
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
      
      for (const file of files) {
        // Check file size
        if (file.size > maxFileSize) {
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'warning',
            color: 'warning',
            icon: 'mdi-alert',
            timeout: 5000,
            closable: true,
            message: `الملف "${file.name}" كبير جداً. الحد الأقصى هو 10 ميجابايت`
          });
          continue;
        }
        
        // Check if file already exists
        const fileExists = this.newMessage.attachments.some(
          existingFile => existingFile.name === file.name && existingFile.size === file.size
        );
        
        if (fileExists) {
          this.$store.dispatch('alerts/showAlert', {
            id: Date.now(),
            type: 'info',
            color: 'info',
            icon: 'mdi-information',
            timeout: 3000,
            closable: true,
            message: `الملف "${file.name}" مضاف مسبقاً`
          });
          continue;
        }
        
        this.newMessage.attachments.push(file);
      }
      
      // Reset file input
      this.$refs.fileInput.value = '';
    },

    getFileIcon(fileName) {
      // Handle null, undefined, or empty filename
      if (!fileName || typeof fileName !== 'string') {
        return 'mdi-file-document';
      }
      
      const extension = fileName.split('.').pop().toLowerCase();
      
      switch (extension) {
        case 'pdf':
          return 'mdi-file-pdf-box';
        case 'doc':
        case 'docx':
          return 'mdi-file-word';
        case 'xls':
        case 'xlsx':
          return 'mdi-file-excel';
        case 'ppt':
        case 'pptx':
          return 'mdi-file-powerpoint';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'mdi-file-image';
        case 'txt':
          return 'mdi-file-document-outline';
        default:
          return 'mdi-file-document';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    selectTemplate(template) {
      this.selectedTemplate = template;
    },
    
    previewTemplate(template) {
      this.previewTemplateData = template;
      this.templatePreviewDialog = true;
    },
    
    applyTemplate() {
      if (this.selectedTemplate) {
        // Fill subject and body from selected template
        this.newMessage.subject = this.selectedTemplate.subject || '';
        this.newMessage.body = this.selectedTemplate.body || this.selectedTemplate.content || '';
        
        // Update the editor content
        if (this.$refs.editor) {
          this.$refs.editor.innerHTML = this.newMessage.body;
        }
        
        this.showTemplateDialog = false;
        
        showSuccessAlert(this.$store, { data: { message: `تم تطبيق القالب: ${this.selectedTemplate.name}` } }, 
          `تم تطبيق القالب: ${this.selectedTemplate.name}`, { icon: 'mdi-file-document-check' });
      }
    },
    
    insertTable() {
      this.tableDialog = true;
    },
    
    insertLink() {
      this.linkDialog = true;
    },
    
    insertImage() {
      // Create a file input for image selection
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (event) => {
        const file = event.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = `<img src="${e.target.result}" alt="صورة" style="max-width: 100%; height: auto; display: block; margin: 10px 0;">`;
            this.insertHTML(img);
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    },
    
    insertChart() {
      // Simple chart insertion - in a real app, you might use a chart library
      const chartHtml = `
        <div style="border: 1px solid #ccc; padding: 10px; margin: 10px 0; background: #f9f9f9; border-radius: 4px;">
          <h4 style="margin: 0 0 10px 0; color: #333;">رسم بياني</h4>
          <div style="height: 200px; background: linear-gradient(45deg, #4CAF50, #2196F3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; border-radius: 4px;">
            [رسم بياني تفاعلي]
          </div>
          <p style="text-align: center; margin-top: 10px; font-size: 12px; color: #666; margin-bottom: 0;">
            سيتم استبدال هذا برسم بياني حقيقي
          </p>
        </div>
      `;
      this.insertHTML(chartHtml);
    },
    
    insertSignature() {
      const signature = `
        <div style="border-top: 2px solid #ccc; margin-top: 20px; padding-top: 10px;">
          <p style="margin: 5px 0;"><strong>مع التحية،</strong></p>
          <p style="margin: 5px 0;">اسم المرسل</p>
          <p style="margin: 5px 0;">المنصب</p>
          <p style="margin: 5px 0;">رقم الهاتف: 123-456-789</p>
          <p style="margin: 5px 0;">البريد الإلكتروني: sender@example.com</p>
        </div>
      `;
      this.insertHTML(signature);
    },
    
    insertTableConfirm() {
      const rows = parseInt(this.tableConfig.rows);
      const cols = parseInt(this.tableConfig.columns);
      
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      
      for (let i = 0; i < rows; i++) {
        tableHtml += '<tr>';
        for (let j = 0; j < cols; j++) {
          tableHtml += `<td style="border: 1px solid #ddd; padding: 8px; text-align: right;">الخلية ${i + 1}-${j + 1}</td>`;
        }
        tableHtml += '</tr>';
      }
      
      tableHtml += '</table>';
      
      this.insertHTML(tableHtml);
      this.tableDialog = false;
    },
    
    insertLinkConfirm() {
      if (this.linkConfig.url && this.linkConfig.text) {
        const linkHtml = `<a href="${this.linkConfig.url}" target="_blank">${this.linkConfig.text}</a>`;
        this.insertHTML(linkHtml);
        this.linkConfig = { url: '', text: '' };
        this.linkDialog = false;
      }
    },
    
    // Helper method to insert HTML at cursor position
    insertHTML(html) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      // Focus the editor first
      editor.focus();
      
      try {
        // Get current selection
        const selection = window.getSelection();
        
        // If no selection, create one at the end of the editor
        if (selection.rangeCount === 0) {
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false); // Collapse to end
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        const range = selection.getRangeAt(0);
        
        // Create a temporary container
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Insert the HTML
        range.deleteContents();
        
        // Insert all child nodes
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }
        range.insertNode(fragment);
        
        // Move cursor after the inserted content
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Update the content
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error inserting HTML:', error);
        // Fallback: append to the end
        editor.innerHTML += html;
        this.updateContent({ target: editor });
      }
    },
    
    // Helper method to insert text at cursor position
    insertText(text) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        const selection = window.getSelection();
        
        // If no selection, create one at the end of the editor
        if (selection.rangeCount === 0) {
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false); // Collapse to end
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        const range = selection.getRangeAt(0);
        
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error inserting text:', error);
        // Fallback: append to the end
        editor.innerHTML += text;
        this.updateContent({ target: editor });
      }
    },
    
    insertList(type) {
      const editor = this.$refs.editor;
      if (!editor) return;
      
      editor.focus();
      
      try {
        const command = type === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList';
        
        if (document.queryCommandSupported && document.queryCommandSupported(command)) {
          document.execCommand(command, false, null);
        } else {
          // Fallback method
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            
            if (selectedText) {
              const listType = type === 'ordered' ? 'ol' : 'ul';
              const list = document.createElement(listType);
              const items = selectedText.split('\n').filter(item => item.trim());
              
              items.forEach(item => {
                const li = document.createElement('li');
                li.textContent = item.trim();
                list.appendChild(li);
              });
              
              range.deleteContents();
              range.insertNode(list);
            }
          }
        }
        
        this.updateContent({ target: editor });
      } catch (error) {
        console.error('Error inserting list:', error);
      }
    },

    // Reply functionality
    handleComposeReply(replyData) {
      //console.log('Handling compose reply with data:', replyData);
      
      // Set reply mode
      this.isReplyMode = true;
      this.replyData = replyData;
      
      // Pre-fill form with reply data
      this.newMessage = {
        to_departments: replyData.to_departments || [],
        cc_departments: replyData.cc_departments || [],
        subject: replyData.subject || '',
        body: '',
        mail_priority_id: null,
        mail_type_id: null,
        attachments: []
      };
      
      // Open compose dialog
      this.showComposeDialog = true;
    },

    closeComposeDialog() {
      this.showComposeDialog = false;
      this.resetForm();
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getContentPreview(content) {
      if (!content) return 'لا يوجد محتوى';
      // Strip HTML tags and limit to 200 characters
      const textContent = content.replace(/<[^>]*>/g, '');
      return textContent.length > 200 ? textContent.substring(0, 200) + '...' : textContent;
    },

    // Forward functionality
    handleComposeForward(forwardData) {
      //console.log('Handling compose forward with data:', forwardData);
      
      // Set forward mode
      this.isForwardMode = true;
      this.forwardData = forwardData;
      
      // Pre-fill form with forward data
      this.newMessage = {
        to_departments: forwardData.to_departments || [],
        cc_departments: forwardData.cc_departments || [],
        subject: forwardData.subject || '',
        body: '',
        mail_priority_id: null,
        mail_type_id: null,
        attachments: []
      };
      
      // Open compose dialog
      this.showComposeDialog = true;
    },

    // Helper methods
    getDialogTitle() {
      if (this.isReplyMode) return 'رد على الرسالة';
      if (this.isForwardMode) return 'إعادة توجيه الرسالة';
      return 'مراسلة جديدة';
    },

    getToFieldHint() {
      if (this.isReplyMode) {
        return 'المستقبل محدد تلقائياً (غير قابل للتعديل)';
      }
      if (this.isForwardMode) {
        return 'اختر الأقسام لإعادة توجيه الرسالة إليها';
      }
      const baseHint = `${this.availableDepartments.length} قسم متاح للإرسال`;
      if (this.isPermissionFiltered) {
        return `${baseHint} (مُفلتر حسب صلاحيات القسم)`;
      }
      return baseHint;
    },

    getCcFieldHint() {
      if (this.isReplyMode) {
        return 'النسخة محددة تلقائياً (غير قابلة للتعديل)';
      }
      if (this.isForwardMode) {
        return 'اختر الأقسام التي ستحصل على نسخة من الرسالة المُعاد توجيهها';
      }
      const baseHint = 'الأقسام التي ستحصل على نسخة من المراسلة';
      if (this.availableDepartments.length === 0) {
        return baseHint + ' - لا توجد أقسام متاحة';
      }
      
      if (this.isPermissionFiltered) {
        return `${baseHint} (${this.availableDepartments.length} قسم متاح حسب الصلاحيات)`;
      }
      return `${baseHint} (${this.availableDepartments.length} قسم متاح)`;
    },

    getOriginalMail() {
      if (this.isReplyMode && this.replyData) {
        return this.replyData.originalMail || {};
      }
      if (this.isForwardMode && this.forwardData) {
        return this.forwardData.originalMail || {};
      }
      return {};
    },

    // Print functionality for original message
    printOriginalMessage() {
      const originalMail = this.getOriginalMail();
      if (!originalMail || !originalMail.id) return;
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        showErrorAlert(this.$store, { message: 'تعذر فتح نافذة الطباعة' }, 'فشل في الطباعة');
        return;
      }
      
      // Get current date for print
      const printDate = new Date().toLocaleDateString('ar-LY', {
        weekday: 'long',
        year: 'numeric', 
        month: 'long',
        day: 'numeric'
      });
      
      // Build the print content
      const printContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>طباعة الرسالة الأصلية - ${originalMail.subject}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              direction: rtl;
              text-align: right;
              margin: 20px;
              line-height: 1.6;
              color: #333;
            }
            
            .print-header {
              text-align: center;
              border-bottom: 2px solid #2196F3;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            
            .print-header h1 {
              color: #2196F3;
              margin: 0;
              font-size: 24px;
            }
            
            .print-header .date {
              color: #666;
              font-size: 14px;
              margin-top: 5px;
            }
            
            .print-header .original-mail-badge {
              display: inline-block;
              background: ${this.isReplyMode ? '#4caf50' : '#2196f3'};
              color: white;
              padding: 8px 16px;
              border-radius: 20px;
              font-size: 14px;
              font-weight: bold;
              margin-top: 10px;
            }
            
            .mail-details {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
              border-right: 4px solid #2196F3;
            }
            
            .mail-details h2 {
              color: #2196F3;
              margin-top: 0;
              font-size: 20px;
              border-bottom: 1px solid #ddd;
              padding-bottom: 10px;
            }
            
            .detail-row {
              margin-bottom: 10px;
              display: flex;
              align-items: center;
            }
            
            .detail-label {
              font-weight: bold;
              color: #444;
              min-width: 120px;
              margin-left: 10px;
            }
            
            .detail-value {
              color: #666;
            }
            
            .priority-chip {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              background: #e3f2fd;
              color: #1976d2;
              font-size: 12px;
              font-weight: bold;
              border: 1px solid #bbdefb;
            }
            
            .urgent-chip {
              background: #ffebee;
              color: #c62828;
              border-color: #ffcdd2;
            }
            
            .mail-content {
              background: white;
              padding: 25px;
              border-radius: 8px;
              border: 1px solid #ddd;
              margin-bottom: 20px;
            }
            
            .mail-content h3 {
              color: #2196F3;
              margin-top: 0;
              margin-bottom: 15px;
              font-size: 18px;
            }
            
            .content-text {
              font-size: 16px;
              line-height: 1.8;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            
            .attachments {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              border-right: 4px solid #4caf50;
            }
            
            .attachments h3 {
              color: #4caf50;
              margin-top: 0;
              margin-bottom: 15px;
            }
            
            .attachment-item {
              display: flex;
              align-items: center;
              padding: 8px 0;
              border-bottom: 1px solid #eee;
            }
            
            .attachment-item:last-child {
              border-bottom: none;
            }
            
            .attachment-icon {
              margin-left: 10px;
              color: #4caf50;
              font-weight: bold;
            }
            
            .print-footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              color: #666;
            }
            
            @media print {
              body { margin: 0; }
              .print-header, .mail-details, .mail-content, .attachments { 
                break-inside: avoid; 
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>نظام إدارة البريد الإلكتروني</h1>
            <div class="date">تاريخ الطباعة: ${printDate}</div>
            <div class="original-mail-badge">
              ${this.isReplyMode ? '🔄 الرسالة الأصلية للرد' : '📤 الرسالة الأصلية للإعادة توجيه'}
            </div>
          </div>
          
          <div class="mail-details">
            <h2>تفاصيل الرسالة الأصلية</h2>
            <div class="detail-row">
              <span class="detail-label">الموضوع:</span>
              <span class="detail-value">${originalMail.subject || 'غير محدد'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">من:</span>
              <span class="detail-value">${originalMail.sender?.name || 'غير محدد'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">القسم المرسل:</span>
              <span class="detail-value">${originalMail.sender_department?.name || 'غير محدد'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">التاريخ:</span>
              <span class="detail-value">${this.formatDate(originalMail.sent_at || originalMail.created_at)}</span>
            </div>
            ${originalMail.priority ? `
            <div class="detail-row">
              <span class="detail-label">الأولوية:</span>
              <span class="priority-chip">${originalMail.priority.name || originalMail.priority}</span>
            </div>` : ''}
          </div>
          
          <div class="mail-content">
            <h3>محتوى الرسالة الأصلية</h3>
            <div class="content-text">${originalMail.body || 'لا يوجد محتوى'}</div>
          </div>
          
          ${originalMail.attachments && originalMail.attachments.length > 0 ? `
          <div class="attachments">
            <h3>المرفقات</h3>
            ${originalMail.attachments.map(attachment => `
              <div class="attachment-item">
                <span class="attachment-icon">📎</span>
                <span>${attachment.file_name || attachment.name || 'ملف غير معروف'}</span>
                ${attachment.file_size ? `<span style="color: #666; margin-right: 10px;">(${attachment.file_size})</span>` : ''}
              </div>
            `).join('')}
          </div>` : ''}
          
          <div class="print-footer">
            <p>تم إنشاء هذه الطباعة من نظام إدارة البريد الإلكتروني</p>
            <p>نوع العملية: ${this.isReplyMode ? 'الرد على الرسالة' : 'إعادة توجيه الرسالة'}</p>
            <p>تاريخ الطباعة: ${new Date().toLocaleString('ar-LY')}</p>
          </div>
        </body>
        </html>
      `;
      
      // Write content to print window
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
          // Close the window after printing (optional)
          printWindow.onafterprint = function() {
            printWindow.close();
          };
        }, 500);
      };
    }
  }
}
</script>

<style scoped>
.v-main {
  background-color: #f8f9fa;
}

.formatting-toolbar {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.editor {
  min-height: 200px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  background-color: white;
  overflow-y: auto;
}

.editor:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.editor[placeholder]:empty:before {
  content: attr(placeholder);
  color: #999;
  font-style: italic;
}

.color-picker-item {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.color-picker-item:hover {
  border-color: #333;
  transform: scale(1.1);
}

.template-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.template-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.template-card--selected {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.05);
}

.template-card .v-card-text {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* RTL specific styles */
.v-btn-group .v-btn:not(:last-child) {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-right: none;
}

.v-btn-group .v-btn:not(:first-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  border-left: none;
}

/* Toolbar button hover effects */
.formatting-toolbar .v-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.formatting-toolbar .v-btn:active {
  background-color: rgba(0, 0, 0, 0.08);
}

/* Table styles */
table {
  direction: rtl;
}

td, th {
  text-align: right;
}

/* Link styles */
a {
  color: #1976d2;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

/* Chart placeholder styles */
.chart-placeholder {
  background: linear-gradient(45deg, #4CAF50, #2196F3);
  color: white;
  text-align: center;
  padding: 20px;
  border-radius: 4px;
  margin: 10px 0;
}

/* Signature styles */
.signature {
  border-top: 2px solid #ccc;
  margin-top: 20px;
  padding-top: 10px;
  font-style: italic;
}

/* Compose dialog specific styles */
.editor {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  background-color: #fafafa;
  transition: border-color 0.3s ease;
}

.editor:focus {
  outline: none;
  border-color: #1976d2;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.editor[placeholder]:empty:before {
  content: attr(placeholder);
  color: #999;
  font-style: italic;
}

/* Attachment chip animations */
.v-chip {
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Action buttons gap */
.gap-2 {
  gap: 8px;
}

/* Custom chip styling for better visibility */
.v-chip--variant-outlined {
  border-width: 1.5px;
}

/* Priority select custom styling */
.v-select .v-chip {
  margin: 2px;
}

/* Form validation styling */
.v-text-field--error .v-field__outline {
  border-color: #f44336 !important;
}

.v-autocomplete--error .v-field__outline {
  border-color: #f44336 !important;
}
</style> 